import { createFeature, createReducer, on } from '@ngrx/store';
import { getErrorMessage } from '@zero/shared/util-global-state';
import { AssetTimeline } from '@zero/shared/util-models';
import { AssetTimelineInfo } from '../entities/asset-timeline-info.entity';
import { Field, PayoutProfile, Underlying } from '../entities/derivative-data.entity';
import { instrumentDetailActions, instrumentDetailApiActions } from './instrument-detail.actions';

export interface InstrumentDetailState {
  assetTimeline: AssetTimeline;
  assetTimelineError: string;
  assetTimelineLoading: boolean;
  assetTimelineInitiallyLoaded: boolean;
  assetTimelineInfo: AssetTimelineInfo;
  derivativeTopData: Field[];
  derivativeBaseData: Field[];
  derivativeKeyData: Field[];
  derivativePayoutProfile: PayoutProfile[];
  derivativeBaseDataVisible: boolean;
  derivativeKeyDataVisible: boolean;
  derivativePayoutProfileVisible: boolean;
  derivativeProductDescription: string;
  derivativeUnderlyingData: Underlying;
}

const initialState: InstrumentDetailState = {
  assetTimeline: null,
  assetTimelineError: null,
  assetTimelineInfo: null,
  assetTimelineLoading: false,
  assetTimelineInitiallyLoaded: false,
  derivativeTopData: null,
  derivativeBaseData: null,
  derivativeKeyData: null,
  derivativePayoutProfile: null,
  derivativeBaseDataVisible: false,
  derivativeKeyDataVisible: false,
  derivativePayoutProfileVisible: false,
  derivativeProductDescription: null,
  derivativeUnderlyingData: null,
};

// Define constants for colors
const ADVANTAGE_STYLE_BLACK = 'black';
const ADVANTAGE_STYLE_RED = 'red';
const ADVANTAGE_STYLE_GREEN = 'green';

/**
 * Determines the advantage style color based on the advantage string.
 * @param advantage - The advantage string to evaluate.
 * @returns The corresponding color as a string.
 */
function getAdvantageStyle(advantage: string): string {
  if (advantage.toLowerCase() === 'direkt') {
    return ADVANTAGE_STYLE_RED;
  } else if (advantage.toLowerCase() === 'derivat') {
    return ADVANTAGE_STYLE_GREEN;
  }
  return ADVANTAGE_STYLE_BLACK;
}

function sortDerivativePayoutProfile(a: PayoutProfile, b: PayoutProfile): number {
  try {
    const aUnderlyingPrice = parseFloat(a.underlyingPrice?.replace(',', '')?.match(/\d+/)[0]);
    const bUnderlyingPrice = parseFloat(b.underlyingPrice?.replace(',', '')?.match(/\d+/)[0]);

    if (isNaN(aUnderlyingPrice) || isNaN(bUnderlyingPrice)) {
      return 0;
    }

    return aUnderlyingPrice - bUnderlyingPrice;
  } catch (error) {
    console.error('Error parsing underlying price:', error);
    return 0;
  }
}

export const instrumentDetailFeature = createFeature({
  name: 'instrumentDetail',
  reducer: createReducer(
    initialState,
    on(
      instrumentDetailApiActions.loadAssetTimelineFromApi,
      (state, { params }): InstrumentDetailState => ({
        ...state,
        assetTimeline: params.isin !== state.assetTimeline?.isin ? null : state.assetTimeline,
        assetTimelineLoading: true,
        assetTimelineError: null,
      }),
    ),
    on(
      instrumentDetailApiActions.loadAssetTimelineSuccess,
      (state, { isin, assetTimeline }): InstrumentDetailState => ({
        ...state,
        assetTimeline: {
          ...assetTimeline,
          isin,
          items: {
            ...assetTimeline.items,
            content: state.assetTimeline?.items?.content
              ? [...state.assetTimeline.items.content, ...assetTimeline.items.content]
              : assetTimeline.items.content,
          },
        },
        assetTimelineInitiallyLoaded: true,
        assetTimelineLoading: false,
      }),
    ),
    on(
      instrumentDetailApiActions.loadAssetTimelineFailure,
      (state, { error }): InstrumentDetailState => ({
        ...state,
        assetTimelineError: getErrorMessage(error),
        assetTimelineInitiallyLoaded: true,
        assetTimelineLoading: false,
      }),
    ),
    on(
      instrumentDetailApiActions.loadAssetTimelineInfoSuccess,
      (state, { assetTimelineInfo }): InstrumentDetailState => ({
        ...state,
        assetTimelineInfo,
      }),
    ),
    on(
      instrumentDetailApiActions.loadAssetTimelineInfoFailure,
      instrumentDetailActions.resetAssetTimeline,
      (state): InstrumentDetailState => ({
        ...state,
        assetTimelineInfo: null,
      }),
    ),
    on(
      instrumentDetailActions.resetAssetTimeline,
      (state): InstrumentDetailState => ({
        ...state,
        assetTimeline: null,
        assetTimelineError: null,
        assetTimelineLoading: false,
        assetTimelineInitiallyLoaded: false,
      }),
    ),
    on(
      instrumentDetailApiActions.loadDerivativeDataSuccess,
      (state, { derivative }): InstrumentDetailState => ({
        ...state,
        derivativeTopData: derivative?.classificationFigures,
        derivativeBaseData: derivative?.masterData,
        derivativeKeyData: derivative?.keyFigures,
        derivativePayoutProfile: Array.isArray(derivative?.payoutProfile)
          ? derivative.payoutProfile.sort(sortDerivativePayoutProfile).map((item) => ({
              ...item,
              advantageStyle: getAdvantageStyle(item?.advantage),
            }))
          : [],
        derivativeProductDescription: derivative?.productDescription,
        derivativeUnderlyingData: derivative?.underlying,
      }),
    ),
    on(
      instrumentDetailActions.resetDerivativeData,
      (state): InstrumentDetailState => ({
        ...state,
        derivativeTopData: null,
        derivativeBaseData: null,
        derivativeKeyData: null,
        derivativeProductDescription: null,
        derivativeUnderlyingData: null,
      }),
    ),
    on(
      instrumentDetailActions.setDerivativeDataVisibility,
      (state, { visibility }): InstrumentDetailState => ({
        ...state,
        derivativeBaseDataVisible: visibility ? visibility.isBaseDataVisible : true,
        derivativeKeyDataVisible: visibility ? visibility.isKeyDataVisible : true,
        derivativePayoutProfileVisible: visibility ? visibility.isPayoutProfileVisible : true,
      }),
    ),
  ),
});
